/**
 * JsonCadLoader - JSON 格式 CAD 加载器
 *
 * 职责：解析 JSON 文本，归一化旧版 CAD 元数据，并转换为 CadLoadResult
 * 取消策略：soft（解析完成后检查 signal）
 */

import { createCadLoadResultFromMetadata } from '@/plugins/cad/deserializeCadMetadata'
import { normalizeJsonCadMetadata } from '@/plugins/cad/normalizeJsonCadMetadata'
import type { CadLoadRequest, CadLoadResult } from '@/plugins/cad/types'
import type { AssetLoader, LoadContext } from '@/shared/types/assets'

/** 取消级别：hard 表示可强制中断，soft 表示仅在完成后检查 */
export type CancellationLevel = 'hard' | 'soft'

/** 从 source 获取 JSON 文本数据 */
async function fetchJsonData(source: CadLoadRequest, signal?: AbortSignal): Promise<string> {
  if (source.data !== undefined) {
    return source.data
  }
  if (source.url) {
    const resp = await fetch(source.url, { signal })
    if (!resp.ok) {
      throw new Error(`JSON fetch failed: ${resp.status} ${resp.statusText}`)
    }
    return resp.text()
  }
  throw new Error('JsonCadLoader: data 和 url 均未提供')
}

/** JSON 格式 CAD 加载器 */
export class JsonCadLoader implements AssetLoader<CadLoadRequest, CadLoadResult> {
  /** 加载器支持的格式标识 */
  readonly format = 'json'
  /** 自动识别优先级，数值越大越先尝试 */
  readonly detectPriority = 0
  /** 取消级别：soft 表示仅在分步完成后检查 signal */
  readonly cancellationLevel: CancellationLevel = 'soft'

  /**
   * 检测来源是否为 JSON CAD 格式。
   *
   * @param source - CAD 加载请求
   * @returns format 显式为 'json' 或 url 以 .json 结尾时返回 true
   */
  canLoad(source: CadLoadRequest): boolean {
    if (source.format === 'json') return true
    if (source.url && /\.json$/i.test(source.url)) return true
    return false
  }

  /**
   * 加载并解析 JSON CAD 数据。
   *
   * @param source - CAD 加载请求，需提供 data 或 url
   * @param context - 加载上下文，用于取消与进度汇报
   * @returns CAD 加载结果
   */
  async load(source: CadLoadRequest, context: LoadContext): Promise<CadLoadResult> {
    const signal = source.signal ?? context.signal

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    const text = await fetchJsonData(source, signal)

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch {
      throw new Error('JsonCadLoader: JSON 解析失败，数据格式无效')
    }

    const metadata = normalizeJsonCadMetadata(parsed)

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    context.onProgress?.(1)
    return createCadLoadResultFromMetadata(metadata)
  }
}
